/**
 * Section copy resolution.
 *
 * Every narrative block on the site reads through here. Stored values from
 * SiteSettings.sections win; anything missing falls back to the deck copy in
 * content.js. That means the site renders correctly before anything has been
 * edited, during an API outage, and after a partial edit — a half-filled
 * section object can never blank out a page.
 *
 * Keeping the shape in one place also lets the admin editor and the renderer
 * agree without either importing the other.
 */
import {
  BUDGET_BANDS,
  ABOUT,
  MISSION,
  VISION,
  MINDSET,
  PROCESS,
  WHY_AGENZ,
  CREATIVE_EXECUTION,
  BRAND,
} from './content.js';

/** The deck defaults, in the exact shape the admin editor writes. */
export function defaultSections() {
  return {
    about: {
      eyebrow: ABOUT.eyebrow,
      headline: BRAND.coverLine,
      headlineAccent: BRAND.coverSub,
      body: ABOUT.body,
    },
    mission: {
      eyebrow: MISSION.eyebrow,
      headline: MISSION.headline,
      body: MISSION.body,
    },
    vision: {
      eyebrow: VISION.eyebrow,
      headline: VISION.headline,
      body: VISION.body,
    },
    mindset: {
      eyebrow: MINDSET.eyebrow,
      headline: MINDSET.headline,
      principles: [...MINDSET.principles],
    },
    process: {
      eyebrow: PROCESS.eyebrow,
      steps: PROCESS.steps.map((s) => ({ ...s })),
    },
    whyAgenz: {
      eyebrow: WHY_AGENZ.eyebrow,
      headline: WHY_AGENZ.headline,
      points: [...WHY_AGENZ.points],
    },
    creativeExecution: {
      eyebrow: CREATIVE_EXECUTION.eyebrow,
      body: CREATIVE_EXECUTION.body,
    },
    marquee: {
      items: [BRAND.tagline, BRAND.coverLine, 'Strategy first', 'Content that performs'],
      speed: 30,
    },
    footer: {
      headline: BRAND.closingLine,
      sub: BRAND.closingSub,
    },
    servicesIndex: {
      title: 'Everything a brand needs to show up and grow.',
      homeHeading: 'What we do',
    },
    workIndex: {
      eyebrow: 'Work',
      title: 'The work, and what it moved.',
      lead:
        "Brands we've helped show up sharper, move faster, and actually perform. " +
        'Every project starts with strategy and ends with numbers.',
      homeHeading: 'Selected work',
      emptyTitle: 'Case studies coming soon',
      emptyBody:
        'The work is being packaged up. In the meantime, the fastest way to see how we ' +
        'think is to talk to us.',
    },
  };
}

/**
 * Merges stored section copy over the defaults.
 *
 * Merges per section and treats empty strings as "not set", so clearing a field
 * in the admin restores the original wording rather than publishing a blank.
 * Arrays are replaced wholesale when present — a list the editor emptied on
 * purpose should stay empty, but an absent list falls back.
 */
export function resolveSections(stored = {}) {
  const defaults = defaultSections();
  const out = {};

  for (const [key, fallback] of Object.entries(defaults)) {
    const override = stored?.[key];

    if (!override || typeof override !== 'object') {
      out[key] = fallback;
      continue;
    }

    const merged = { ...fallback };
    for (const [field, value] of Object.entries(override)) {
      if (value === undefined || value === null) continue;
      if (typeof value === 'string' && value.trim() === '') continue;
      if (Array.isArray(value) && value.length === 0) continue;
      merged[field] = value;
    }
    out[key] = merged;
  }

  return out;
}

export default resolveSections;


/**
 * Defaults for the hero, closing CTA and contact form.
 *
 * These previously lived inline in the components, which meant the admin had no
 * way to know the current value of a field nobody had overridden — the editor
 * opened blank on copy that was plainly visible on the page. Resolving them
 * server-side alongside the sections makes 'what is on the page' and 'what the
 * editor shows' the same thing.
 */
export function defaultHeroCopy() {
  return {
    line1: 'Built to make',
    line2: 'brands',
    emphasis: 'move.',
    sub: BRAND.heroSub,
    primaryLabel: 'View the work',
    primaryHref: '/work',
    secondaryLabel: 'Start a project',
    secondaryHref: '/contact',
  };
}

export function defaultCta() {
  return {
    headline: BRAND.closingSub,
    sub: null,
    buttonLabel: "Let's build it",
    buttonHref: '/contact',
  };
}

export function defaultContactForm() {
  return {
    heading: 'Tell us about the brand',
    intro: 'The more context you give us, the sharper our first response will be.',
    messageLabel: 'What are you trying to do?',
    submitLabel: 'Send it',
    successTitle: 'Message sent',
    successBody: 'Got it. We will come back to you within two working days.',
    budgetBands: [...BUDGET_BANDS],
  };
}

/** Same merge rule as sections: blanks fall back, explicit values win. */
function mergeOver(defaults, stored = {}) {
  const out = { ...defaults };
  for (const [k, v] of Object.entries(stored ?? {})) {
    if (v === undefined || v === null) continue;
    if (typeof v === 'string' && v.trim() === '') continue;
    if (Array.isArray(v) && v.length === 0) continue;
    out[k] = v;
  }
  return out;
}

export const resolveHeroCopy = (stored) => mergeOver(defaultHeroCopy(), stored);
export const resolveCta = (stored) => mergeOver(defaultCta(), stored);
export const resolveContactForm = (stored) => mergeOver(defaultContactForm(), stored);
